/**
 * Strips posthog-js's vestigial `ip` query parameter from requests to the
 * PostHog host before they leave the page.
 *
 * Why: generic filter-list rules match PostHog's URL signature on ANY domain,
 * so the /ph reverse proxy alone does not get past uBlock Origin / EasyPrivacy:
 *
 *   uBO privacy.txt   /e/*^ip=*^compression=     /e/*^ip=*^beacon=
 *   EasyPrivacy       /?compression=*&ip=
 *
 * Every one of them needs `ip=`. posthog-js appends it to every request, yet
 * its own types mark the option "THIS OPTION HAS NO EFFECT"
 * (@posthog/types posthog-config.d.ts), so PostHog ignores it and nothing
 * restores it at the edge. IP and GeoIP come from the request itself.
 *
 * Only XHR and sendBeacon are wrapped, and initPostHog sets
 * `api_transport: 'XHR'`. posthog-js captures `window.fetch` once when its
 * module evaluates (utils/globals.js), which in the production bundle is
 * before any app code can run, so a fetch wrapper would silently never apply.
 * XHR requests are opened through the shared XMLHttpRequest.prototype.open and
 * sendBeacon is looked up at call time, so patching both right before
 * posthog.init covers every request regardless of load order.
 */

/** Removes `ip=` from `url` if it targets `host`; otherwise returns `url` untouched. */
export function stripIpParam(url: string, host: string, pageHref: string): string {
  const q = url.indexOf('?')
  if (q === -1) return url

  let target: URL
  let base: URL
  try {
    target = new URL(url, pageHref)
    base = new URL(host, pageHref)
  } catch {
    return url
  }
  const basePath = base.pathname.replace(/\/$/, '')
  const onHost =
    target.origin === base.origin &&
    (basePath === '' || target.pathname === basePath || target.pathname.startsWith(basePath + '/'))
  if (!onHost) return url

  // Edit the raw query rather than re-serializing through URLSearchParams, so
  // every other parameter keeps its exact encoding.
  const hash = url.indexOf('#', q)
  const query = url.slice(q + 1, hash === -1 ? undefined : hash)
  const kept = query.split('&').filter((part) => part !== 'ip' && !part.startsWith('ip='))
  if (kept.length === query.split('&').length) return url
  const rest = hash === -1 ? '' : url.slice(hash)
  return url.slice(0, q) + (kept.length ? '?' + kept.join('&') : '') + rest
}

const INSTALLED = Symbol.for('edge.phTransport')

/** Wraps XHR open and sendBeacon so PostHog requests go out without `ip=`. Idempotent. */
export function installPhTransport(host: string, win: Window & typeof globalThis = window): void {
  const marker = win as unknown as Record<symbol, boolean>
  if (!host || marker[INSTALLED]) return
  marker[INSTALLED] = true

  const rewrite = (url: string | URL): string => stripIpParam(String(url), host, win.location.href)

  const xhrProto = win.XMLHttpRequest?.prototype
  if (xhrProto) {
    const origOpen = xhrProto.open as (...args: unknown[]) => void
    xhrProto.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      return origOpen.call(this, method, rewrite(url), ...rest)
    } as typeof xhrProto.open
  }

  const nav = win.navigator
  if (nav && typeof nav.sendBeacon === 'function') {
    const origBeacon = nav.sendBeacon.bind(nav)
    nav.sendBeacon = (url: string | URL, data?: BodyInit | null) => origBeacon(rewrite(url), data)
  }
}
